import SHA256 from 'crypto-js/sha256';
import { getAnonymousID, getSessionID } from 'redline-client-sdk';
import { useRedline } from '@libs/redline';
import { getRDG } from '@libs/utils/helpers/getRDG';
import { useOmniAds } from '@libs/utils/hooks/OmniAds';
import { getApp } from '@libs/utils/helpers/getApplication';
import { getCookie } from '@libs/utils/helpers/getSafeCookies';
import { useGoogleAnalytics } from '@libs/utils/hooks/useGoogleAnalytics';

import { roasMap } from './roasMap';

import {
  SHOffer,
  ProductClickedPayload,
  RedirectRequestPayload,
  ProposalStartedPayload,
  ElementEventPayload,
  ProductViewedPayload,
  ProductListViewedPayload,
  ProductListFilteredPayload,
  DecisionOutcomeReceivedPayload,
  MMDecisionProcessedProps,
  formEvent,
  FormContinuedEventPayload,
  FormSubmittedEventPayload,
  FormErroredEventPayload,
  FieldInputtedEventPayload,
} from './types';

const GOOGLE_VALUE_MULTIPLIER = 10;

export const useCreditTrack = () => {
  const { track, getCorrelationId } = useRedline();
  const { trackGA } = useGoogleAnalytics();
  const { omniTracker } = useOmniAds();

  const correlationId = getCorrelationId('RL_TRACK_CID');
  const currentApp = getApp() === 'acordocerto' ? 'ac' : 'cp';

  function productClickedEvent({ offer, index, location, document, outboundUrl }: ProductClickedPayload) {
    const eventName = 'default-productClicked';
    const { origin } = window.location;

    const payload = {
      correlationId: correlationId,
      listId: `${currentApp}-logado-ofertas-${location}`,
      experience: 'logged',
      product: {
        brand: offer?.generalInfo?.partner,
        category: offer?.generalInfo?.category,
        location: location,
        name: offer?.content?.name,
        position: index,
        productId: offer?.content?.slug,
        text: offer?.content?.title,
        pageId: offer?.content?.slug,
        variant: offer?.content?.variant,
      },
      viewCorrelationId: correlationId,
      rdg: getRDG(document || ''),
      actionOutcome: outboundUrl?.includes(origin) ? 'internal-link' : 'external-link',
      outboundUrl,
    };

    track.ecommerce.productClicked(payload);
    trackGA({ eventName, payload });
  }

  function redirectRequestedEvent({ fireboltId, offerSlug, originPath, redirectURL, document }: RedirectRequestPayload) {
    const payload = {
      originId: originPath,
      redirectRequestedId: offerSlug,
      redirectRequestedUrl: redirectURL,
      fireboltId: fireboltId,
      rdg: getRDG(document || ''),
    };

    track.experience.redirectRequested(payload);
  }

  async function proposalStartedEvent({ offer, redirectUrl, fireboltId, document, email }: ProposalStartedPayload) {
    const startSourceUrl = getCookie('start_source_url');
    const offerSlug = offer?.content?.slug || '';
    const roas = roasMap[offerSlug as keyof typeof roasMap];
    const internName = 'credit.proposalStarted.v1';

    if (roas != null && roas > 0) {
      const googleValue = roas * GOOGLE_VALUE_MULTIPLIER;

      const googlePayload = {
        transaction_id: offer?.content._uid,
        value: googleValue,
        currency: 'BRL',
        intern_name: internName,
        items: [
          {
            item_id: offerSlug,
            item_name: offer?.content?.title,
            item_category: offer?.generalInfo?.category,
            price: roas,
            quantity: 1,
          },
        ],
      };

      const metaPayload = {
        currency: 'BRL',
        value: roas,
        order_id: offer?.content._uid,
        content_ids: [offerSlug],
        content_type: 'product',
        content_name: offer?.content?.title,
        num_items: 1,
      };

      omniTracker.all({
        google: {
          target: 'gA4',
          eventName: 'purchase',
          payload: googlePayload,
        },
        meta: {
          eventName: 'Purchase',
          payload: metaPayload,
        },
      });
    }

    const payload = {
      fireboltId,
      initialSlug: offerSlug,
      finalSlug: offerSlug,
      category: offer?.generalInfo?.category,
      redirectUrl,
      startSourceUrl,
      rdg: getRDG(document || ''),
      marketplace: currentApp,
      experience: 'logged',
      value: roas || 0,
      currency: 'BRL',
      intern_name: internName,
    };

    // TODO: Remove GA call after complete migration to OmniAds
    trackGA({ eventName: 'proposalStarted', payload });
    return track.experience.proposalStarted(payload);
  }

  function elementClickedEvent(eventPayload: ElementEventPayload) {
    const eventName = 'default-elementClicked';

    track.userTracking.elementClicked(eventPayload);
    trackGA({ eventName, payload: { webElement: eventPayload } });
  }

  function elementViewedEvent(eventPayload?: ElementEventPayload) {
    const eventName = 'default-elementViewed';

    track.userTracking.elementViewed(eventPayload);
    trackGA({ eventName, payload: { webElement: eventPayload } });
  }

  function productViewedEvent({ offer, index, location, document }: ProductViewedPayload) {
    const eventName = 'default-productViewed';

    const payload = {
      listId: `${currentApp}-logado-ofertas-${location}`,
      product: {
        brand: offer?.generalInfo.partner,
        category: offer?.generalInfo.category,
        location: location,
        name: offer?.metadata.name,
        position: index,
        productId: offer?.generalInfo.slug,
        text: offer?.content?.title,
        variant: offer?.content?.variant,
        partner: offer?.generalInfo.partner,
        price: Number(offer?.generalInfo.limit) || 0,
      },
      experience: 'logged',
      viewCorrelationId: correlationId,
      rdg: getRDG(document || ''),
    };

    const isMMOffer = Boolean(offer?.matchMaker?.slug);
    if (isMMOffer) {
      const mmText = offer?.matchMaker?.featureDescriptions?.map((feature) => `${feature.title}: ${feature.description}`).join(', ');
      payload.product.brand = offer?.matchMaker?.productName || '';
      payload.product.category = offer?.matchMaker?.category || '';
      payload.product.text = mmText || '';
    }

    track.ecommerce.productViewed(payload);
    trackGA({ eventName, payload });
  }

  function elementRenderedEvent(eventPayload?: ElementEventPayload) {
    const eventName = 'default-elementRendered';

    track.userTracking.elementRendered(eventPayload);
    trackGA({ eventName, payload: { webElement: eventPayload } });
  }

  function productListViewedEvent({ listId, initialCategory, products }: ProductListViewedPayload) {
    if (!products) return;

    const allProducts = products?.map((product, index) => {
      const { content } = product;
      return {
        pageId: listId,
        productId: content?.slug,
        name: content?.title,
        category: content?.category,
        brand: content?.partner,
        variant: content?.variant,
        position: index,
      };
    });

    const payload = {
      correlationId: '',
      listName: listId,
      listId: listId,
      category: initialCategory,
      product: allProducts,
    };

    track.ecommerce.productListViewed(payload);
  }

  function productListFilteredEvent({ currentCategory, listId, filteredOffers, filterType }: ProductListFilteredPayload) {
    const products = filteredOffers?.map((offer, index) => ({
      brand: offer?.content?.partner,
      category: currentCategory,
      name: offer?.content?.title,
      variant: offer?.content?.variant,
      productId: offer?.content?.slug,
      pageId: listId,
      position: index,
    }));

    const payload = {
      listId: listId,
      filters: [
        {
          type: filterType,
          value: currentCategory,
        },
      ],
      product: products,
    };
    track.ecommerce.productListFiltered(payload);
  }

  function productPreApprovedEvent(offers: SHOffer[]) {
    if (!offers || offers.length < 1) return;
    const preApprovedPartners = ['caixa', 'santander', 'jeitto', 'bb', 'neon'];

    const filteredOffers = offers.filter((offer) => preApprovedPartners.includes(offer?.generalInfo?.partner));
    if (filteredOffers.length < 1) return;

    const userAgent = window?.navigator?.userAgent || '';
    const sessionId = getSessionID();
    const anonymousId = getAnonymousID();
    const location = window?.location?.href;

    filteredOffers.forEach((offer) => {
      const eventName = `ecommerce.ProductPreApproved${offer?.generalInfo?.partner}.v1`;
      const payload = {
        productSlug: offer?.content?.slug,
        partner: offer?.generalInfo?.partner,
        userAgent,
        sessionId,
        anonymousId,
        location,
      };

      track.ecommerce.productPreApproved(payload);
      trackGA({ eventName, payload });
    });
  }

  function decisionEnrichedEvent() {
    const payload = {
      properties: {
        partner: currentApp,
      },
    };
    track.sortingHat.decisionEnriched(payload);
    trackGA({ eventName: 'default-shDecisionEnriched', payload });
  }

  function decisionRequestedEvent() {
    const payload = {
      properties: {
        partner: currentApp,
      },
    };
    track.sortingHat.decisionRequested(payload);
    trackGA({ eventName: 'default-shDecisionRequested', payload });
  }

  function decisionOutcomeReceivedEvent({ workflowId, decisionProducts, isDefaultDecision }: DecisionOutcomeReceivedPayload) {
    const payload = {
      partner: currentApp,
      workflowId,
      decisionProducts,
      isDefaultDecision,
    };
    track.sortingHat.decisionOutcomeReceived(payload);
    trackGA({ eventName: 'default-shDecisionOutcomeReceived', payload });
  }

  function MMDecisionProcessedEvent({ decision, formData, document }: MMDecisionProcessedProps) {
    const filters = [
      {
        key: 'benefit',
        value: formData?.benefit,
      },
      {
        key: 'anuity',
        value: formData?.annuity,
      },
    ];

    const products = decision.map((item: any) => ({
      slug: item.slug || item.matchMaker?.slug,
      category: item.category || item.matchMaker?.category,
      partner: item.partner || item.matchMaker?.partner,
      metadata: [
        {
          key: 'cashbackPercentage',
          value: item.rewardsHighlights?.cashback || item.matchMaker?.rewardsHighlights?.cashback,
        },
        {
          key: 'milesPercentage',
          value: item.rewardsHighlights?.miles || item.matchMaker?.rewardsHighlights?.miles,
        },
        {
          key: 'approvalChance',
          value: item.approvalChance || item.matchMaker?.approvalChance,
        },
      ],
    }));

    const payload = {
      identifier: SHA256(document.replace(/\D/g, '')).toString(),
      listId: "mm-logged",
      decisionContext: {
        filters,
        products,
      },
    };

    track.matchmaker.decisionProcessed(payload);
    trackGA({ eventName: 'MMdecisionProcessed', payload });
  }
  function formViewedEvent(payload: formEvent) {
    const eventName = 'formViewed';

    track.userTracking.formViewed(payload);
    trackGA({ eventName, payload: payload });
  }

  function formContinuedEvent({ formContext, capturedData }: FormContinuedEventPayload) {
    const eventName = 'formContinued';

    const payload = {
      ...formContext,
      fields: capturedData,
    };

    track.userTracking.formContinued(payload);
    trackGA({ eventName, payload: payload });
  }

  function formBackedEvent(payload: formEvent) {
    const eventName = 'formBacked';

    track.userTracking.formBacked(payload);
    trackGA({ eventName, payload: payload });
  }

  function formSubmittedEvent({ formContext, allFields }: FormSubmittedEventPayload) {
    const eventName = 'formSubmitted';
    const field = Object.entries(allFields).map(([key, value]) => {
      return { fieldName: key, fieldValue: value };
    });
    const payload = {
      ...formContext,
      field,
    };

    track.userTracking.formSubmitted(payload);
    trackGA({ eventName, payload });
  }

  function formErroredEvent({ formContext, errorMessage }: FormErroredEventPayload) {
    const eventName = 'formErrored';

    const finalPayload = {
      ...formContext,
      errorDetails: errorMessage,
    };

    track.userTracking.formErrored(finalPayload);
    trackGA({ eventName, payload: formContext });
  }

    function fieldInputtedEvent({formContext, stepContext, userContext}: FieldInputtedEventPayload) {
    const eventName = 'fieldInputted';

    const payload = {
      formContext,
      stepContext,
      userInputtedField: {
        fieldSource: "user-input",
        ...userContext
      }
    };

    track.userTracking.fieldInputted(payload);
    trackGA({ eventName, payload });
  }

  return {
    productClickedEvent,
    redirectRequestedEvent,
    proposalStartedEvent,
    elementClickedEvent,
    elementViewedEvent,
    elementRenderedEvent,
    productViewedEvent,
    productListViewedEvent,
    productListFilteredEvent,
    productPreApprovedEvent,
    decisionEnrichedEvent,
    decisionRequestedEvent,
    decisionOutcomeReceivedEvent,
    MMDecisionProcessedEvent,
    formViewedEvent,
    formContinuedEvent,
    formBackedEvent,
    formSubmittedEvent,
    formErroredEvent,
    fieldInputtedEvent
  };
};
